import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LucideCircleCheckBig, LucideGraduationCap, LucideLock, provideLucideIcons } from '@lucide/angular';
import { environment } from '../../../environments/environment';
import { buildPageTitle } from '../../shared/constants/site-meta';
import { AuthService } from '../../services/auth.service';
import { AdminIconComponent } from '../../shared/admin';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              width?: string;
            },
          ) => void;
        };
      };
    };
  }
}

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, AdminIconComponent],
  providers: [provideLucideIcons(LucideGraduationCap, LucideCircleCheckBig, LucideLock)],
  host: {
    style: 'display: block; width: 100%; max-width: 100%;',
  },
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss',
})
export class LoginPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly buttonLabel = computed(() => (this.loading() ? 'Authenticating...' : 'Continue with Google'));

  async ngOnInit(): Promise<void> {
    this.title.setTitle(buildPageTitle('Sign In'));
    this.errorMessage.set(null);

    const restored = await this.authService.restoreSession();
    if (restored) {
      const redirect =
        this.authService.userType() === 'tenant' ? '/tenant/dashboard' : '/platform/dashboard';
      await this.router.navigateByUrl(redirect);
      return;
    }

    try {
      await this.ensureGoogleScript();
      this.initGoogleButton();
    } catch {
      this.errorMessage.set('Google sign-in is currently unavailable. Please try again shortly.');
    }
  }

  private async ensureGoogleScript(): Promise<void> {
    if (window.google?.accounts?.id) {
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity="true"]');
    if (existing) {
      await new Promise<void>((resolve) => {
        existing.addEventListener('load', () => resolve(), { once: true });
      });
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset['googleIdentity'] = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google SDK'));
      document.head.appendChild(script);
    });
  }

  private initGoogleButton(): void {
    const effectiveClientId = environment.googleClientId;
    if (!effectiveClientId || effectiveClientId === 'GOOGLE_CLIENT_ID') {
      this.errorMessage.set('Google client ID is not configured.');
      return;
    }

    const google = window.google;
    const buttonContainer = document.getElementById('googleSignInButton');

    if (!google?.accounts?.id || !buttonContainer) {
      this.errorMessage.set('Google sign-in failed to initialize.');
      return;
    }

    google.accounts.id.initialize({
      client_id: effectiveClientId,
      callback: (response) => {
        if (!response.credential) {
          this.errorMessage.set('Authentication failed. Please try again.');
          return;
        }
        void this.handleGoogleCredential(response.credential);
      },
    });

    buttonContainer.innerHTML = '';
    google.accounts.id.renderButton(buttonContainer, {
      theme: 'outline',
      size: 'large',
      shape: 'rectangular',
      text: 'continue_with',
      width: String(this.googleButtonWidth()),
    });
  }

  private googleButtonWidth(): number {
    const max = 510;
    const min = 260;
    if (typeof window === 'undefined') {
      return max;
    }
    const container = document.getElementById('googleSignInButton')?.parentElement;
    const available = container?.clientWidth ?? window.innerWidth - 48;
    return Math.min(max, Math.max(min, Math.floor(available)));
  }

  private async handleGoogleCredential(googleToken: string): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);

    try {
      const { redirectTo } = await this.authService.login(googleToken);
      await this.router.navigateByUrl(redirectTo);
    } catch {
      this.errorMessage.set('Authentication failed. Please use an authorized account.');
    } finally {
      this.loading.set(false);
    }
  }
}
